import React, { useEffect, useRef, useState } from 'react'

const SPEEDS = [1, 1.5, 2]
const SKIP_SECONDS = 10

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds)) return '0:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}

const Icon = ({ children, size = 18 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true">
    {children}
  </svg>
)

const PlayIcon = () => (
  <Icon>
    <path
      d="M8 5.5v13a1 1 0 0 0 1.5.86l10.5-6.5a1 1 0 0 0 0-1.72L9.5 4.64A1 1 0 0 0 8 5.5z"
      fill="currentColor"
    />
  </Icon>
)

const PauseIcon = () => (
  <Icon>
    <rect x="6" y="5" width="4" height="14" rx="1" fill="currentColor" />
    <rect x="14" y="5" width="4" height="14" rx="1" fill="currentColor" />
  </Icon>
)

const SkipIcon = ({ forward }) => (
  <Icon size={26}>
    <g
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      transform={forward ? 'matrix(-1 0 0 1 24 0)' : undefined}
    >
      <path d="M4 12a8 8 0 1 0 2.5-5.8" />
      <path d="M4 4v4h4" />
    </g>
    <text
      x="12"
      y="15"
      textAnchor="middle"
      fontSize="7"
      fontWeight="700"
      fill="currentColor"
      style={{ fontFamily: "var(--font-family-base)" }}
    >
      {SKIP_SECONDS}
    </text>
  </Icon>
)

export const PostAudio = ({ src }) => {
  const audioRef = useRef(null)
  const [playing, setPlaying] = useState(false)
  const [current, setCurrent] = useState(0)
  const [duration, setDuration] = useState(0)
  const [speed, setSpeed] = useState(1)
  const [failed, setFailed] = useState(false)

  // Metadata / errors may arrive before hydration attaches the handlers
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return
    if (audio.error) setFailed(true)
    if (audio.duration) setDuration(audio.duration)
  }, [])

  const togglePlay = () => {
    const audio = audioRef.current
    if (audio.paused) audio.play()
    else audio.pause()
  }

  const seekTo = (time) => {
    const audio = audioRef.current
    const clamped = Math.min(Math.max(time, 0), duration || 0)
    audio.currentTime = clamped
    setCurrent(clamped)
  }

  const cycleSpeed = () => {
    const next = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length]
    audioRef.current.playbackRate = next
    setSpeed(next)
  }

  const progress = duration ? (current / duration) * 100 : 0

  return (
    <div className={`post-audio ${playing ? 'playing' : ''}`}>
      <audio
        ref={audioRef}
        preload="metadata"
        src={src}
        onError={() => setFailed(true)}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onDurationChange={(e) => setDuration(e.target.duration)}
        onTimeUpdate={(e) => setCurrent(e.target.currentTime)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      />

      {failed ? (
        <div className="post-audio-failed">
          Audio tidak dapat diputar.{' '}
          <a href={src} target="_blank" rel="noreferrer">
            Buka file audio
          </a>
        </div>
      ) : (
        <>
          <div className="post-audio-header">
            <span className="post-audio-label">
              <span className="post-audio-dot" />
              Dengarkan
            </span>
            <time className="post-audio-time">
              {formatTime(current)} / {formatTime(duration)}
            </time>
          </div>

          <input
            type="range"
            className="post-audio-seek"
            min="0"
            max={duration || 0}
            step="0.1"
            value={current}
            onChange={(e) => seekTo(Number(e.target.value))}
            style={{ '--progress': `${progress}%` }}
            aria-label="Posisi audio"
          />

          <div className="post-audio-controls">
            <div className="post-audio-controls-side" />
            <div className="post-audio-controls-main">
              <button
                type="button"
                className="post-audio-skip"
                onClick={() => seekTo(current - SKIP_SECONDS)}
                aria-label={`Mundur ${SKIP_SECONDS} detik`}
              >
                <SkipIcon />
              </button>
              <button
                type="button"
                className="post-audio-play"
                onClick={togglePlay}
                aria-label={playing ? 'Jeda' : 'Putar'}
              >
                {playing ? <PauseIcon /> : <PlayIcon />}
              </button>
              <button
                type="button"
                className="post-audio-skip"
                onClick={() => seekTo(current + SKIP_SECONDS)}
                aria-label={`Maju ${SKIP_SECONDS} detik`}
              >
                <SkipIcon forward />
              </button>
            </div>
            <div className="post-audio-controls-side end">
              <button
                type="button"
                className="post-audio-speed"
                onClick={cycleSpeed}
                aria-label={`Kecepatan putar ${speed}×`}
              >
                {speed}×
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
